import { useI18n } from '@/i18n'
import { Minus, Plus, X } from '@/shared/icons/phosphor'
import { NumberInput as ArkNumberInput } from '@ark-ui/solid/number-input'
import { type Component, Show } from 'solid-js'

export interface NumberInputProps {
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  min?: number
  max?: number
  step?: number
  allowEmpty?: boolean
  emptyLabel?: string
}

export const NumberInput: Component<NumberInputProps> = (props) => {
  const { t } = useI18n()
  const isEmpty = () => !props.value || props.value === ''
  const showEmptyState = () => props.allowEmpty && isEmpty()

  const handleClear = () => {
    props.onChange?.('')
  }

  return (
    <Show
      when={!showEmptyState()}
      fallback={
        <button
          type="button"
          onClick={() => props.onChange?.('1')}
          disabled={props.disabled}
          class={`
            w-full
            flex items-center justify-between
            rounded-[var(--radius-md)]
            border border-dashed border-[var(--color-field-border)]
            bg-[var(--color-surface)]
            px-3 py-2
            text-[length:var(--text-base)]
            font-[family-name:var(--font-sans)]
            text-[color:var(--color-text-muted)]
            transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
            hover:border-[var(--color-primary-300)]
            hover:text-[color:var(--color-primary-600)]
            hover:bg-[var(--color-primary-50)]
            disabled:opacity-50 disabled:cursor-not-allowed
          `.trim()}
        >
          <span>
            {props.emptyLabel ?? props.placeholder ?? t('designSystem.numberInput.noLimit')}
          </span>
          <span class="text-xs font-medium">{t('designSystem.numberInput.setLimit')}</span>
        </button>
      }
    >
      <div class="flex items-center gap-2">
        <ArkNumberInput.Root
          class="flex-1 min-w-0"
          value={props.value}
          onValueChange={(details) => props.onChange?.(details.value)}
          min={props.min ?? 1}
          max={props.max}
          step={props.step ?? 1}
          allowOverflow={false}
          clampValueOnBlur
          disabled={props.disabled}
        >
          <ArkNumberInput.Control
            class={`
              flex items-center flex-1
              rounded-[var(--radius-md)]
              border border-[var(--color-field-border)]
              bg-[var(--color-surface)]
              transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
              focus-within:border-[var(--color-primary-400)]
              focus-within:shadow-[var(--focus-ring)]
            `.trim()}
          >
            <ArkNumberInput.DecrementTrigger
              class={`
                flex items-center justify-center
                h-10 w-10 shrink-0
                border-r border-[var(--color-border)]
                text-[color:var(--color-text-muted)]
                transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
                hover:bg-[var(--color-neutral-100)]
                hover:text-[color:var(--color-text)]
                active:bg-[var(--color-neutral-200)]
                disabled:opacity-30 disabled:cursor-not-allowed
                disabled:hover:bg-transparent disabled:hover:text-[color:var(--color-text-muted)]
                rounded-l-[var(--radius-md)]
              `.trim()}
            >
              <Minus class="w-3.5 h-3.5" />
            </ArkNumberInput.DecrementTrigger>

            <ArkNumberInput.Input
              class={`
                flex-1 min-w-0
                text-center
                px-3 py-2
                text-[length:var(--text-base)]
                font-[family-name:var(--font-sans)]
                text-[color:var(--color-text)]
                bg-transparent
                placeholder:text-[color:var(--color-text-subtle)]
                focus:outline-none
                disabled:opacity-50 disabled:cursor-not-allowed
              `.trim()}
              placeholder={props.placeholder}
            />

            <ArkNumberInput.IncrementTrigger
              class={`
                flex items-center justify-center
                h-10 w-10 shrink-0
                border-l border-[var(--color-border)]
                text-[color:var(--color-text-muted)]
                transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
                hover:bg-[var(--color-neutral-100)]
                hover:text-[color:var(--color-text)]
                active:bg-[var(--color-neutral-200)]
                disabled:opacity-30 disabled:cursor-not-allowed
                disabled:hover:bg-transparent disabled:hover:text-[color:var(--color-text-muted)]
                rounded-r-[var(--radius-md)]
              `.trim()}
            >
              <Plus class="w-3.5 h-3.5" />
            </ArkNumberInput.IncrementTrigger>
          </ArkNumberInput.Control>
        </ArkNumberInput.Root>

        <Show when={props.allowEmpty}>
          <button
            type="button"
            onClick={handleClear}
            disabled={props.disabled}
            class={`
              flex items-center justify-center
              h-10 w-10 shrink-0
              rounded-[var(--radius-md)]
              border border-[var(--color-field-border)]
              bg-[var(--color-surface)]
              text-[color:var(--color-text-muted)]
              transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
              hover:bg-[var(--color-error-50)]
              hover:border-[var(--color-error-200)]
              hover:text-[color:var(--color-error)]
              disabled:opacity-50 disabled:cursor-not-allowed
            `.trim()}
            title={t('designSystem.numberInput.removeLimit')}
          >
            <X class="w-3.5 h-3.5" />
          </button>
        </Show>
      </div>
    </Show>
  )
}
